// SECURITY: Single source of truth for auth and customer state (MVVM model layer)
// ASSUMPTION: AWS Cognito, getCustomer, and getUser APIs are configured; currentCustomer persisted in localStorage.
// ASSUMPTION: getUser(email) returns profile fields; customer / role-like fields in that body are informational vs JWT authz.
// RISK: Authentication state affects access to protected resources; signOut clears sensitive data

import { getCurrentUserInfoV5Shaped, signOut as sessionSignOut, ensureAuthenticatedUser } from "@/auth/amplifyAuth";
import { graphqlRequest } from "@/graphql/client";
import { action, computed, flow, makeObservable, observable } from "mobx";

import { AUTH_ROLES } from "@/constants/auth";
import { clearNotificationStorageForScope } from "@/context/notificationPersistence";
import { IS_DEV } from "@/utils/env";
import { resetUserflow } from "@/utils/userflow";
import { getUserProfileNameParts } from "@/utils/getUserProfileNameParts";
import {
  clear as clearImpersonationSession,
  start as startImpersonationSession,
  validateAgainstCognito,
} from "@/impersonation/impersonationSession";
import { getAgent, getCustomer, getFiles, getUser } from "../graphql/queries";

const CURRENT_CUSTOMER_KEY = "currentCustomer";
const CUSTOMER_ID_KEY = "customer_id";
const CUSTOMER_OBJ_KEY = "customer_obj";
/** Persisted active-role ("view") choice; always re-validated against Cognito groups on restore. */
const ACTIVE_ROLE_KEY = "tars_active_role_v1";

/** Role priority order — single source of truth for role ranking and the view switcher list. */
const ROLE_PRIORITY = [
  AUTH_ROLES.SUPERADMIN,
  AUTH_ROLES.CAMPAIGN,
  AUTH_ROLES.REALTOR,
  AUTH_ROLES.HOMEOWNER,
  AUTH_ROLES.INVESTOR,
  AUTH_ROLES.CONTRACTOR,
  AUTH_ROLES.WHOLESALER,
  AUTH_ROLES.USER,
];

class AuthStore {
  isLoading = true;
  isError = false;
  error = "";
  userId = "";
  username = "";
  email = "";
  customer = "";
  accounts = [];
  files = [];
  agents = [];
  cognitoUserGroups = [];
  userProfile = null;
  userProfileLoaded = false;
  /** The single role currently being embodied ("view"). Only settable from {@link availableRoles}. */
  activeRole = "";

  _cognitoUserId = "";
  _cognitoUsername = "";
  _cognitoEmail = "";
  _cognitoUserGroups = [];
  _impersonationPayload = null;

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      isError: observable,
      error: observable,
      userId: observable,
      username: observable,
      email: observable,
      customer: observable,
      accounts: observable,
      files: observable,
      agents: observable,
      cognitoUserGroups: observable,
      userProfile: observable,
      userProfileLoaded: observable,
      activeRole: observable,
      availableRoles: computed,
      setActiveRole: action,
      patchUserProfileMetadata: action,
      _impersonationPayload: observable,
      getGlobalInformation: flow,
      fetchUserProfile: flow,
      fetchAccounts: flow,
      fetchFiles: flow,
      fetchAgents: flow,
      startImpersonation: flow,
      stopImpersonation: flow,
      clear: action,
      signOut: action,
      setCustomer: action,
      firstname: computed,
      lastname: computed,
      fullName: computed,
      isDisplayNamePending: computed,
      role: computed,
      groups: computed,
      isSuperAdmin: computed,
      cognitoIsSuperAdmin: computed,
      isCampaignUser: computed,
      isRealtor: computed,
      isInvestor: computed,
      isHomeowner: computed,
      isUser: computed,
      isContractor: computed,
      isWholesaler: computed,
      _currentAccount: computed,
      isImpersonating: computed,
      cognitoUserId: computed,
    });
  }

  /**
   * First matching {@link AUTH_ROLES} value from a list of identifiers (Cognito User Group names or profile role strings).
   * @param {unknown} identifiers
   * @returns {string}
   */
  _appRoleFromRoleIdentifiers(identifiers) {
    if (!Array.isArray(identifiers) || identifiers.length === 0) return "";
    const g = identifiers.map((x) => String(x).trim().toLowerCase());
    return ROLE_PRIORITY.find((r) => g.includes(r)) || "";
  }

  /** Priority-ordered, deduped list of recognized application roles from a raw identifier list. */
  _orderRoles(identifiers) {
    if (!Array.isArray(identifiers) || identifiers.length === 0) return [];
    const g = new Set(identifiers.map((x) => String(x).trim().toLowerCase()).filter(Boolean));
    return ROLE_PRIORITY.filter((r) => g.has(r));
  }

  /**
   * Active role default on identity (re)apply: persisted choice when still valid, else first
   * recognized role by priority.
   * SECURITY: Never trusts a stored value outside the current Cognito-sourced role list (fail-closed).
   */
  _resolveActiveRole() {
    const ordered = this._orderRoles(this.cognitoUserGroups);
    if (ordered.length === 0) return "";
    const stored = this._readActiveRole();
    return stored && ordered.includes(stored) ? stored : ordered[0];
  }

  _readActiveRole() {
    if (typeof localStorage === "undefined") return "";
    try {
      return String(localStorage.getItem(ACTIVE_ROLE_KEY) ?? "")
        .trim()
        .toLowerCase();
    } catch {
      return "";
    }
  }

  _writeActiveRole(role) {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(ACTIVE_ROLE_KEY, role);
    } catch {
      // ignore (private mode / quota)
    }
  }

  _normalizeRoleList(groups) {
    if (!Array.isArray(groups)) return [];
    return groups
      .map((item) =>
        String(item ?? "")
          .trim()
          .toLowerCase()
      )
      .filter(Boolean);
  }

  _applyEffectiveIdentityFromCognito() {
    this.userId = this._cognitoUserId;
    this.username = this._cognitoUsername;
    this.email = this._cognitoEmail;
    this.cognitoUserGroups = [...this._cognitoUserGroups];
    this.activeRole = this._resolveActiveRole();
  }

  _applyImpersonationPayload(payload) {
    if (!payload) return;
    const targetId = String(payload.targetUserId ?? "").trim();
    const targetEmail = String(payload.targetEmail ?? "").trim();
    const targetRoles = this._normalizeRoleList(payload.targetRoles);
    this._impersonationPayload = payload;
    this.userId = targetId || targetEmail;
    this.username = this.userId;
    this.email = targetEmail || this._cognitoEmail;
    this.cognitoUserGroups = targetRoles.length > 0 ? targetRoles : [AUTH_ROLES.USER];
    this.activeRole = this._resolveActiveRole();
  }

  _clearImpersonationState() {
    this._impersonationPayload = null;
    this._applyEffectiveIdentityFromCognito();
    if (this.activeRole) {
      this._writeActiveRole(this.activeRole);
    }
  }

  get cognitoUserId() {
    return this._cognitoUserId;
  }

  get groups() {
    return this.cognitoUserGroups;
  }

  get isImpersonating() {
    return this._impersonationPayload != null;
  }

  get isSuperAdmin() {
    return this.cognitoUserGroups.includes(AUTH_ROLES.SUPERADMIN);
  }

  get cognitoIsSuperAdmin() {
    return this._cognitoUserGroups.includes(AUTH_ROLES.SUPERADMIN);
  }

  /** @returns {boolean} True if the active (embodied) role is campaign. */
  get isCampaignUser() {
    return this.activeRole === AUTH_ROLES.CAMPAIGN;
  }

  /** @returns {boolean} True if the active (embodied) role is realtor. */
  get isRealtor() {
    return this.activeRole === AUTH_ROLES.REALTOR;
  }

  /** @returns {boolean} True if the active (embodied) role is investor. */
  get isInvestor() {
    return this.activeRole === AUTH_ROLES.INVESTOR;
  }

  /** @returns {boolean} True if the active (embodied) role is homeowner. */
  get isHomeowner() {
    return this.activeRole === AUTH_ROLES.HOMEOWNER;
  }

  /** @returns {boolean} True if the active (embodied) role is user. */
  get isUser() {
    return this.activeRole === AUTH_ROLES.USER;
  }

  /** @returns {boolean} True if the active (embodied) role is contractor. */
  get isContractor() {
    return this.activeRole === AUTH_ROLES.CONTRACTOR;
  }

  /** @returns {boolean} True if the active (embodied) role is wholesaler. */
  get isWholesaler() {
    return this.activeRole === AUTH_ROLES.WHOLESALER;
  }

  /** @returns {Object|null} Current account from accounts list matching customer id. */
  get _currentAccount() {
    const customerId = String(this.customer ?? "").trim();
    return this.accounts?.find((a) => String(a.id ?? a.name ?? "") === customerId) ?? null;
  }

  /**
   * Internal helper to resolve a display name from an account-like object.
   * @private
   */
  _resolveName(acc) {
    if (!acc) return "";
    const first = String(acc.first_name ?? acc.firstname ?? acc.firstName ?? "").trim();
    const last = String(acc.last_name ?? acc.lastname ?? acc.lastName ?? "").trim();
    const full = `${first} ${last}`.trim();
    return full || String(acc.name ?? "").trim();
  }

  /** First name: getUser profile first, then current account. */
  get firstname() {
    const { first } = getUserProfileNameParts(this.userProfile);
    if (first) return first;
    const acc = this._currentAccount;
    return String(acc?.firstname ?? acc?.firstName ?? "").trim();
  }

  /** Last name: getUser profile first, then current account. */
  get lastname() {
    const { last } = getUserProfileNameParts(this.userProfile);
    if (last) return last;
    const acc = this._currentAccount;
    return String(acc?.lastname ?? acc?.lastName ?? "").trim();
  }

  /** Full display name (profile → account → username → effective email → "User"). */
  get fullName() {
    const { full } = getUserProfileNameParts(this.userProfile);
    if (full) return full;
    return (
      this._resolveName(this._currentAccount) || this.username || String(this.getEffectiveEmail() ?? "").trim() || "User"
    );
  }

  /** True while the signed-in user's display name is still awaiting the first profile fetch. */
  get isDisplayNamePending() {
    return Boolean(String(this.userId ?? "").trim()) && !this.userProfileLoaded;
  }

  /**
   * Primary application role for the current effective user — the active ("embodied") role.
   * Falls back to first matching group only while {@link activeRole} is not yet resolved.
   */
  get role() {
    return this.activeRole || this._appRoleFromRoleIdentifiers(this.cognitoUserGroups);
  }

  /** Roles the user may switch to ("views"), from the effective Cognito groups, in priority order. */
  get availableRoles() {
    return this._orderRoles(this.cognitoUserGroups);
  }

  /**
   * Switches the active ("embodied") role. Fail-closed: only accepts roles present in the
   * effective Cognito group list (no-ops otherwise) and persists the choice for next session.
   * @param {string} role - AUTH_ROLES value.
   */
  setActiveRole(role) {
    const normalized = String(role ?? "")
      .trim()
      .toLowerCase();
    if (!this.availableRoles.includes(normalized)) return;
    if (normalized !== this.activeRole) {
      this.activeRole = normalized;
    }
    this._writeActiveRole(normalized);
  }

  /**
   * Patches local userProfile.metadata after a client-side profile save.
   * Preserves string vs object metadata wire shape from the loaded profile.
   *
   * @param {object} metadata
   */
  patchUserProfileMetadata(metadata) {
    if (!this.userProfile || typeof this.userProfile !== "object" || !metadata) return;
    const priorMetadata = this.userProfile.metadata;
    const nextMetadata =
      typeof priorMetadata === "string" ? JSON.stringify(metadata) : metadata;
    this.userProfile = { ...this.userProfile, metadata: nextMetadata };
  }

  /**
   * Resets the store state to its initial values and purges sensitive PII from storage.
   * SECURITY: Clears authentication state from memory and also clears registration/targets PII
   * from both localStorage and sessionStorage to prevent data leakage after sign-out.
   */
  clear() {
    this.isLoading = true;
    this.isError = false;
    this.error = "";
    this.userId = "";
    this.username = "";
    this.email = "";
    this.customer = "";
    this.accounts = [];
    this.files = [];
    this.agents = [];
    this.cognitoUserGroups = [];
    this.userProfile = null;
    this.userProfileLoaded = false;
    this.activeRole = "";
    this._cognitoUserId = "";
    this._cognitoUsername = "";
    this._cognitoEmail = "";
    this._cognitoUserGroups = [];
    this._impersonationPayload = null;
    // SECURITY: Clear registration/targets PII from storage on sign-out or auth failure.
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(CURRENT_CUSTOMER_KEY);
      localStorage.removeItem(CUSTOMER_ID_KEY);
      localStorage.removeItem(CUSTOMER_OBJ_KEY);
      localStorage.removeItem(ACTIVE_ROLE_KEY);
      clearImpersonationSession();
    }
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(CUSTOMER_ID_KEY);
      sessionStorage.removeItem(CUSTOMER_OBJ_KEY);
    }
  }

  /**
   * Fetches user profile via getUser(email). Email must match effective principal.
   * Does not set customer or groups from the response; Cognito remains authoritative.
   * Safe no-op if email missing or request fails.
   * SECURITY: Uses AMAZON_COGNITO_USER_POOLS; failures clear userProfile only, not session.
   */
  /**
   * Email from Cognito session only (for getUser argument). Never uses userProfile.email.
   * @returns {string}
   */
  _getContactEmail() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const userId = this.email;
    const fallback = this.userId;
    const email = String(this.email || "").trim();
    if (emailRegex.test(userId)) return userId;
    if (emailRegex.test(fallback)) return fallback;
    if (emailRegex.test(email)) return email;
    return "";
  }

  *fetchUserProfile() {
    try {
      const email = this._getContactEmail();
      if (!email) {
        this.userProfile = null;
        return;
      }
      /** @type {{ email: string, customer_id?: string }} */
      const variables = { email };
      const cust = String(this.customer ?? "").trim();
      if (cust) variables.customer_id = cust;
      const response = yield graphqlRequest({
        query: getUser,
        variables,
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const payload = response?.data?.getUser;
      if (!payload) {
        this.userProfile = null;
        return;
      }
      const status = String(payload.statusCode ?? "");
      let body;
      try {
        body = typeof payload.body === "string" ? JSON.parse(payload.body) : payload.body;
      } catch {
        this.userProfile = null;
        return;
      }
      if (status !== "200" || body == null || typeof body !== "object") {
        this.userProfile = null;
        this.investorPrefsStore?.loadFromProfile?.(null, { isInvestor: this.isInvestor });
        return;
      }
      this.userProfile = body;
      this.investorPrefsStore?.loadFromProfile?.(this.userProfile, { isInvestor: this.isInvestor });

      if (IS_DEV) {
        const profCustomer = String(body.customer ?? "").trim();
        const cognitoCust = String(this.customer ?? "").trim();
        if (profCustomer && cognitoCust && profCustomer !== cognitoCust) {
          console.warn("[AuthStore] getUser customer differs from Cognito custom:account (ignored)");
        }
        const profRoleList = Array.isArray(body.groups) ? body.groups.map(String).sort() : [];
        const cognitoRoleList = [...this._cognitoUserGroups].map(String).sort();
        if (
          profRoleList.length > 0 &&
          cognitoRoleList.length > 0 &&
          JSON.stringify(profRoleList) !== JSON.stringify(cognitoRoleList)
        ) {
          console.warn("[AuthStore] getUser profile role list differs from Cognito User Groups (JWT used for authz)");
        }
      }
    } catch {
      this.userProfile = null;
      this.investorPrefsStore?.loadFromProfile?.(null, { isInvestor: this.isInvestor });
    } finally {
      this.userProfileLoaded = true;
    }
  }

  /**
   * Load customer accounts list (getCustomer API). Call after getGlobalInformation.
   * SECURITY: Fetches account data with AMAZON_COGNITO_USER_POOLS; errors set isError.
   */
  *fetchAccounts() {
    try {
      this.isError = false;
      const response = yield graphqlRequest({
        query: getCustomer,
        variables: { id: "", status: "" },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const rawBody = response.data?.getCustomer?.body;
      let body = [];
      if (rawBody != null && rawBody !== "") {
        const parsed = typeof rawBody === "string" ? JSON.parse(rawBody) : rawBody;
        body = Array.isArray(parsed) ? parsed : [];
      }
      this.accounts = body;
      // Superadmin with no Cognito custom:account and no stored tenant: pick first list entry so inbox/APIs get a customer id.
      const noCustomer = !String(this.customer ?? "").trim();
      if (noCustomer && this.accounts.length > 0 && this.cognitoIsSuperAdmin) {
        const first = this.accounts[0];
        const defaultId = String(first?.id ?? first?.name ?? "").trim();
        if (defaultId) {
          this.setCustomer(defaultId);
        }
      }
    } catch {
      this.isError = true;
      this.accounts = [];
    }
  }

  /**
   * Sign out from Cognito and clear store state. View is responsible for redirect (e.g. navigate("/signin")).
   * SECURITY: Clears authentication state; does not perform navigation (MVVM: view handles routing).
   */
  signOut() {
    const uid = this._cognitoUserId;
    const cust = this.customer;
    const effectiveInboxUser = String(this.userId ?? "").trim();
    return sessionSignOut()
      .then(() => {
        clearNotificationStorageForScope(uid, cust);
        if (effectiveInboxUser && effectiveInboxUser !== String(uid ?? "").trim()) {
          clearNotificationStorageForScope(effectiveInboxUser, cust);
        }
        resetUserflow();
      })
      .catch((err) => {
        // REQ-AUTH-1: Log signOut failure in development instead of swallowing.
        if (IS_DEV) {
          console.error("[AuthStore] signOut failed:", err);
        }
      })
      .finally(() => {
        this.clear();
      });
  }

  /**
   * Set current customer and persist to localStorage. Empty string restores from storage or keeps current.
   * SECURITY: Updates customer only; no privilege escalation; persistence key is constant.
   */
  setCustomer(newCustomer) {
    const customerToSet =
      newCustomer === "" || newCustomer == null
        ? (typeof localStorage !== "undefined" && localStorage.getItem(CURRENT_CUSTOMER_KEY)) || this.customer
        : newCustomer;
    if (customerToSet !== this.customer) {
      this.customer = customerToSet;
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(CURRENT_CUSTOMER_KEY, this.customer);
      }
      const payloadCustomer = String(this._impersonationPayload?.customerId ?? "").trim();
      if (payloadCustomer && payloadCustomer !== String(this.customer ?? "").trim()) {
        clearImpersonationSession();
        this._clearImpersonationState();
      }
    }
  }

  /** @returns {string} Signed-in Cognito username (never impersonated). */
  getSignedInUserId() {
    return String(this._cognitoUserId ?? "").trim();
  }

  /**
   * Email from the signed-in Cognito session only (for getUser and audit-style calls).
   * @returns {string}
   */
  getSignedInEmail() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const userId = String(this._cognitoUserId ?? "").trim();
    const email = String(this._cognitoEmail ?? "").trim();
    if (emailRegex.test(userId)) return userId;
    if (emailRegex.test(email)) return email;
    return "";
  }

  /**
   * Fetches Cognito session (Cognito User Groups, attributes), optionally hydrates profile via getUser.
   * This is the primary entry point for establishing user context on app load.
   * SECURITY: Correctly handles superadmin tenant overrides (CWE-501) while ensuring regular
   * users are pinned to their assigned Cognito account.
   * @param {{ forceRefresh?: boolean, fetchProfile?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  *getGlobalInformation(opts = {}) {
    const { forceRefresh = false, fetchProfile = false } = opts;
    try {
      const userInfo = yield getCurrentUserInfoV5Shaped({ forceRefresh });

      const cognitoUserGroups = this._normalizeRoleList(
        userInfo?.signInUserSession?.accessToken?.payload?.["cognito:groups"] || []
      );
      const isSuperAdmin = cognitoUserGroups.includes(AUTH_ROLES.SUPERADMIN);
      // SECURITY: Only superadmins may override tenant via localStorage (CWE-501).
      // Non-superadmin users always use their Cognito-assigned account.
      const cognitoAccount = userInfo?.attributes?.["custom:account"];
      const storedCustomer = typeof localStorage !== "undefined" && localStorage.getItem(CURRENT_CUSTOMER_KEY);
      this.customer = isSuperAdmin && storedCustomer ? storedCustomer : cognitoAccount;
      const cognitoUsername = String(userInfo?.username ?? "").trim();
      this._cognitoUserId = cognitoUsername;
      this._cognitoUsername = cognitoUsername;
      this._cognitoEmail = String(userInfo?.attributes?.email ?? "").trim();
      this._cognitoUserGroups = cognitoUserGroups;
      this._applyEffectiveIdentityFromCognito();
      this.isLoading = false;

      const payload = isSuperAdmin
        ? validateAgainstCognito({
            realUserId: this._cognitoUserId,
            realEmail: this._cognitoEmail,
            customerId: this.customer,
          })
        : null;
      if (payload) {
        this._applyImpersonationPayload(payload);
      } else {
        clearImpersonationSession();
        this._clearImpersonationState();
      }

      if (fetchProfile) {
        yield this.fetchUserProfile();
      }

      if (IS_DEV) {
        console.log("[AuthStore] user information", {
          userId: this.userId,
          username: this.username,
          email: this.email,
          customer: this.customer,
          fullName: this.fullName,
          role: this.role,
          isImpersonating: this.isImpersonating,
          ...(this.isImpersonating
            ? {
                targetUserId: this.userId,
                targetEmail: this.email,
                targetRoles: [...this.cognitoUserGroups],
              }
            : {}),
        });
      }
    } catch {
      this.clear();
    }
  }

  /**
   * Fetches user-specific files via GraphQL API (getFiles query).
   * SECURITY: Correctly uses authenticated user's ID to scope file retrieval.
   * @returns {Promise<void>}
   */
  *fetchFiles() {
    try {
      this.isError = false;
      const response = yield graphqlRequest({
        query: getFiles,
        variables: { user_id: this.userId },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const rawFiles = response.data?.getFiles?.body;
      let fetchedFiles = [];
      if (rawFiles != null && rawFiles !== "") {
        try {
          const parsed = typeof rawFiles === "string" ? JSON.parse(rawFiles) : rawFiles;
          fetchedFiles = Array.isArray(parsed) ? parsed : [];
        } catch {
          fetchedFiles = [];
        }
      }
      this.files = fetchedFiles;
    } catch {
      this.isError = true;
    }
  }

  /**
   * Fetches agent data associated with the current customer account.
   * @returns {Promise<Array<Object>>} A promise that resolves to the list of fetched agents or an empty array.
   * SECURITY: Uses user-specific ID (domain) and current customer to scope the request.
   */
  *fetchAgents() {
    const params = {
      customer_id: this.customer,
      domain: this.userId,
      provider: "",
      group: false,
    };
    try {
      this.isError = false;
      const response = yield graphqlRequest({
        query: getAgent,
        variables: params,
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const rawAgents = response.data?.getAgent?.body;
      let agentList = [];
      if (rawAgents != null && rawAgents !== "") {
        try {
          const parsed = typeof rawAgents === "string" ? JSON.parse(rawAgents) : rawAgents;
          agentList = Array.isArray(parsed) ? parsed : [];
        } catch {
          agentList = [];
        }
      }
      this.agents = agentList;
      return agentList;
    } catch {
      this.isError = true;
      return [];
    }
  }

  /**
   * Verifies the user is currently authenticated with the auth provider.
   * Use before sensitive API calls that require a valid session.
   * @returns {Promise<void>}
   * @throws {Error} If not authenticated or session invalid
   */
  async ensureAuthenticated() {
    await ensureAuthenticatedUser();
  }

  async start() {
    await this.getGlobalInformation({ forceRefresh: false, fetchProfile: true });
  }

  *startImpersonation(selection) {
    if (!this.cognitoIsSuperAdmin) return;
    const payload = startImpersonationSession({
      realUserId: this._cognitoUserId,
      realEmail: this._cognitoEmail,
      customerId: this.customer,
      target: selection,
    });
    if (!payload) {
      yield this.stopImpersonation();
      return;
    }
    this._applyImpersonationPayload(payload);
    this.userProfileLoaded = false;
    yield this.fetchUserProfile();
  }

  *stopImpersonation() {
    clearImpersonationSession();
    this._clearImpersonationState();
    this.userProfileLoaded = false;
    yield this.fetchUserProfile();
  }

  /**
   * Identifies the best email address for communication (userId if it's an email, else Cognito email).
   * @returns {string} The resolved email address or an empty string.
   */
  /**
   * Preferred email for UI (getUser body when present, else Cognito).
   * getUser GraphQL uses _getCognitoContactEmail only.
   */
  getEffectiveEmail() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const fromProfile = String(this.userProfile?.email ?? "").trim();
    if (emailRegex.test(fromProfile)) return fromProfile;
    return this._getContactEmail();
  }

  /**
   * Returns display name for the current user or for a given account (upstream: firstname/lastname or name).
   * Single source of truth for full-name display in sidebar and user card.
   * @param {Object} [account] - Optional account object (e.g. from getCustomer) with firstname, lastname, or name.
   * @returns {string} Non-empty display name (e.g. "Eric Heilborn", email fallback, "User").
   */
  getDisplayName(account) {
    return account ? this._resolveName(account) || "Unknown" : this.fullName;
  }

  /**
   * Resolves the current customer ID based on hierarchical precedence (property override -> store state -> storage).
   * @param {string} [propertyCustomer=""] - Optional customer ID from a specific property object.
   * @returns {string} The active customer ID or an empty string.
   */
  getEffectiveCustomerId(propertyCustomer = "") {
    const propCust = String(propertyCustomer || "").trim();
    if (propCust) return propCust;

    const storeCust = String(this.customer || "").trim();
    if (storeCust) return storeCust;

    try {
      return String(localStorage.getItem(CURRENT_CUSTOMER_KEY) || "").trim();
    } catch {
      return "";
    }
  }

  /** @returns {string} Current role (delegates to role getter for backward compatibility). */
  currentRole() {
    return this.role;
  }

  /**
   * True if the effective user has the given application role via Cognito User Group membership.
   * @param {string} role - Role to check (e.g., from AUTH_ROLES).
   * @returns {boolean} True if the effective user has the specified role.
   */
  hasRole(role) {
    const normalizedRole = String(role || "")
      .trim()
      .toLowerCase();
    if (!normalizedRole) return false;

    return this.cognitoUserGroups.includes(normalizedRole);
  }

  /**
   * True if the active role may distribute properties (homeowner or realtor view).
   * @returns {boolean} True if the current view has distribution privileges.
   */
  canDistributeProperty() {
    return this.activeRole === AUTH_ROLES.HOMEOWNER || this.activeRole === AUTH_ROLES.REALTOR;
  }
}

export default AuthStore;
